import { useState } from "react";
import Logo from '../components/Logo';
import { useNavigate } from "react-router-dom";
import { useCampaign } from "../context/CampaignContext";

const navItems = [
  { id:"home",      icon:"🏠", label:"Home",          path:"/dashboard" },
  { id:"build",     icon:"⚙️", label:"Build Campaign", path:"/setup" },
  { id:"guide",     icon:"📋", label:"Launch Guide",   path:"/launch-guide" },
  { id:"templates", icon:"📁", label:"Templates",      path:"/dashboard" },
  { id:"profile",   icon:"👤", label:"Profile",        path:"/dashboard" },
];

export default function Screen5() {
  const navigate = useNavigate();
  const { campaign } = useCampaign();

  // Dynamic campaign data from context
  const CAMPAIGN     = { name: campaign.name, type: campaign.type, goal: campaign.goal, location: campaign.location, budget: campaign.budget, mode: campaign.mode };
  const AD_GROUPS    = campaign.adGroups;
  const NEGATIVE_KWS = campaign.negativeKeywords;
  const AD_COPY      = { headlines: campaign.headlines, descriptions: campaign.descriptions, ctas: campaign.ctas };
  const SETTINGS = [
    { label:"Network",         value:"Google Search only",         why:"Keeps your ads on Google Search only — no wasted spend on partner sites.", warn:false },
    { label:"Display Network", value:"Off",                        why:"Display Network often wastes budget for beginners — keep it off.", warn:true },
    { label:"Language",        value:"English",                    why:"Matches the language your target audience searches in.", warn:false },
    { label:"Ad Schedule",     value:"Run all day to start",       why:"Gather data across all hours before restricting your schedule.", warn:false },
    { label:"Location",        value:campaign.location,            why:"Focused targeting gives better results with a small budget.", warn:false },
  ];
  const [activeNav, setActiveNav]         = useState("build");
  const [openGroup, setOpenGroup]         = useState({ ag1:true, ag2:false, ag3:false });
  const [copied, setCopied]               = useState({});

  const copyText = (key, text) => {
    navigator.clipboard?.writeText(text).catch(()=>{});
    setCopied(s=>({...s,[key]:true}));
    setTimeout(()=>setCopied(s=>({...s,[key]:false})),2000);
  };

  const CopyBtn = ({text,copyKey,label,small}) => {
    const done = copied[copyKey];
    return <button style={{width: small?"auto":"100%",padding: small?"6px 10px":"10px 14px",borderRadius:8,border:"1.5px solid",fontSize: small?11:13,fontWeight:700,cursor:"pointer",fontFamily:"sans-serif",background: done?"#dcfce7":"#eef2ff",color: done?"#166534":"#6366f1",borderColor: done?"#bbf7d0":"#c7d2fe",marginTop:4}} onClick={()=>copyText(copyKey,text)}>{done?"✅ Copied!": small?"📋":`📋 ${label||"Copy"}`}</button>;
  };

  const Section = ({title,hint,children}) => {
    const [open,setOpen]=useState(true);
    return <div style={{background:"#fff",borderRadius:16,border:"1px solid #f1f5f9",overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,0.04)"}}>
      <button style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px",background:"none",border:"none",cursor:"pointer",textAlign:"left"}} onClick={()=>setOpen(v=>!v)}>
        <span style={{fontSize:15,fontWeight:700,color:"#1e293b",fontFamily:"sans-serif"}}>{title}</span>
        <span style={{fontSize:14,color:"#94a3b8",transform: open?"rotate(180deg)":"rotate(0deg)",display:"block",transition:"transform 0.25s"}}>▾</span>
      </button>
      {open && <div style={{padding:"0 20px 20px"}}>
        {hint && <p style={{fontSize:12,color:"#64748b",fontFamily:"sans-serif",margin:"0 0 14px",padding:"8px 12px",background:"#f0f9ff",borderRadius:8,border:"1px solid #bae6fd"}}>{hint}</p>}
        {children}
      </div>}
    </div>;
  };

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button style={s.backBtn} onClick={()=>navigate(-1)}>← Back</button>
          <Logo size={32} showText={true} textSize={16} />
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={s.stepPill}>Step 4 of 7</span>
          <div style={s.avatar}>AN</div>
        </div>
      </header>

      <div style={{background:"#fff",borderBottom:"1px solid #f1f5f9",padding:"10px 28px"}}>
        <div style={s.barBg}><div style={{...s.barFill,width:"57%"}} /></div>
      </div>

      <div style={s.body}>
        {/* Confirmation */}
        <div style={s.confirmBlock}>
          <div style={{flex:1,minWidth:240}}>
            <div style={{fontSize:11,fontWeight:800,color:"rgba(255,255,255,0.8)",fontFamily:"sans-serif",letterSpacing:"0.5px",marginBottom:10}}>✅ Campaign Draft Ready</div>
            <h1 style={{fontSize:26,fontWeight:800,color:"#fff",margin:"0 0 10px",lineHeight:1.2}}>Your Campaign Draft Is Ready</h1>
            <p style={{fontSize:14,color:"rgba(255,255,255,0.85)",fontFamily:"sans-serif",lineHeight:1.55,margin:"0 0 8px"}}>We've created a beginner-friendly campaign structure based on your business, goal, and budget.</p>
            <p style={{fontSize:13,color:"rgba(255,255,255,0.7)",fontFamily:"sans-serif"}}>💡 You can edit anything before launch.</p>
          </div>
          <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
            {[["3","Ad Groups"],["18","Keywords"],["9","Headlines"],["3","Descriptions"]].map(([n,l])=>(
              <div key={l} style={{background:"rgba(255,255,255,0.15)",borderRadius:14,padding:"14px 20px",textAlign:"center",minWidth:70}}>
                <div style={{fontSize:28,fontWeight:800,color:"#fff",fontFamily:"sans-serif",lineHeight:1}}>{n}</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.75)",fontFamily:"sans-serif",marginTop:4}}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={s.twoCol}>
          {/* Left */}
          <div style={s.colLeft}>
            <Section title="📋 Campaign Summary">
              <div style={{background:"#f8faff",borderRadius:12,overflow:"hidden",marginBottom:12}}>
                {[["Campaign Name",CAMPAIGN.name,true],["Type",CAMPAIGN.type,false],["Goal",CAMPAIGN.goal,false],["Location",CAMPAIGN.location,false],["Daily Budget",CAMPAIGN.budget,false],["Setup Mode",CAMPAIGN.mode,false]].map(([lbl,val,hi])=>(
                  <div key={lbl} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"10px 14px",borderBottom:"1px solid #f1f5f9",gap:12}}>
                    <span style={{fontSize:12,color:"#94a3b8",fontFamily:"sans-serif",flexShrink:0}}>{lbl}</span>
                    <span style={{fontSize:13,fontFamily:"sans-serif",textAlign:"right",color: hi?"#6366f1":"#1e293b",fontWeight: hi?800:600}}>{val}</span>
                  </div>
                ))}
              </div>
              <CopyBtn text={`Campaign Name: ${CAMPAIGN.name}\nType: ${CAMPAIGN.type}\nGoal: ${CAMPAIGN.goal}\nLocation: ${CAMPAIGN.location}\nBudget: ${CAMPAIGN.budget}`} copyKey="summary" label="Copy Campaign Summary" />
            </Section>
            <Section title="⚙️ Recommended Settings">
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {SETTINGS.map(item=>(
                  <div key={item.label} style={{borderRadius:10,border:`1px solid ${item.warn?"#fbbf24":"#f1f5f9"}`,padding:"12px 14px",background: item.warn?"#fffbeb":"#fff"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                      <span style={{fontSize:13,fontWeight:700,color:"#334155",fontFamily:"sans-serif"}}>{item.label}</span>
                      <span style={{fontSize:13,fontWeight:700,color: item.warn?"#b45309":"#6366f1",fontFamily:"sans-serif"}}>{item.value}</span>
                    </div>
                    <div style={{fontSize:12,color:"#64748b",fontFamily:"sans-serif",lineHeight:1.4}}>{item.why}</div>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          {/* Right */}
          <div style={s.colRight}>
            <Section title="📁 Ad Groups" hint="Ad groups keep similar keywords and ads organised together.">
              {campaign.isSmartGenerated && (
                <div style={{background:"linear-gradient(135deg,#f0fdf4,#dcfce7)",border:"1px solid #bbf7d0",borderRadius:10,padding:"10px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:16}}>✨</span>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:"#166534",fontFamily:"sans-serif"}}>Smart Keywords Generated</div>
                    <div style={{fontSize:12,color:"#15803d",fontFamily:"sans-serif",marginTop:2}}>Keywords are personalised from your offering and location</div>
                  </div>
                </div>
              )}
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {AD_GROUPS.map((grp,idx)=>(
                  <div key={grp.id} style={{borderRadius:12,border:`1.5px solid ${openGroup[grp.id]?"#6366f1":"#e2e8f0"}`}}>
                    <button style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",background:"none",border:"none",cursor:"pointer",textAlign:"left"}} onClick={()=>setOpenGroup(s=>({...s,[grp.id]:!s[grp.id]}))}>
                      <div style={{display:"flex",alignItems:"center",gap:12,flex:1}}>
                        <div style={{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,#6366f1,#818cf8)",color:"#fff",fontWeight:800,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif",flexShrink:0}}>{idx+1}</div>
                        <div>
                          <div style={{fontSize:14,fontWeight:700,color:"#1e293b",fontFamily:"sans-serif",display:"flex",alignItems:"center",gap:8}}>
                            {grp.name}
                            {grp.isSmartGenerated && <span style={{fontSize:10,fontWeight:700,color:"#059669",background:"#dcfce7",border:"1px solid #bbf7d0",borderRadius:20,padding:"2px 8px",fontFamily:"sans-serif"}}>✨ Smart</span>}
                          </div>
                          <div style={{fontSize:12,color:"#64748b",fontFamily:"sans-serif",marginTop:2}}>{grp.desc}</div>
                        </div>
                      </div>
                      <span style={{fontSize:14,color:"#94a3b8",transform: openGroup[grp.id]?"rotate(180deg)":"rotate(0deg)",display:"block"}}>▾</span>
                    </button>
                    {openGroup[grp.id] && <div style={{padding:"0 16px 16px",borderTop:"1px solid #f1f5f9"}}>
                      <div style={{display:"flex",flexWrap:"wrap",gap:8,margin:"12px 0"}}>
                        {grp.keywords.map(kw=><span key={kw} style={{fontSize:12,fontFamily:"sans-serif",color:"#4f46e5",background:"#eef2ff",border:"1px solid #c7d2fe",borderRadius:20,padding:"4px 12px"}}>{kw}</span>)}
                      </div>
                      <CopyBtn text={grp.keywords.join("\n")} copyKey={`kw_${grp.id}`} label={`Copy ${grp.name} Keywords`} small />
                    </div>}
                  </div>
                ))}
              </div>
            </Section>
            <Section title="🚫 Negative Keywords" hint="These prevent your ad from showing to the wrong people.">
              <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:12}}>
                {NEGATIVE_KWS.map(kw=><span key={kw} style={{fontSize:12,fontFamily:"sans-serif",color:"#be123c",background:"#fff1f2",border:"1px solid #fecdd3",borderRadius:20,padding:"4px 12px"}}>{kw}</span>)}
              </div>
              <CopyBtn text={NEGATIVE_KWS.join("\n")} copyKey="neg_kw" label="Copy All Negative Keywords" />
            </Section>
          </div>
        </div>

        {/* Ad Copy */}
        <div style={{marginBottom:28}}>
          <Section title="✍️ Your Ad Copy" hint="Ready to paste directly into Google Ads. Character limits are respected.">
            <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
              <div style={{flex:"1 1 220px",minWidth:200}}>
                <div style={{fontSize:14,fontWeight:700,color:"#1e293b",fontFamily:"sans-serif",marginBottom:4}}>Headlines</div>
                <div style={{fontSize:11,color:"#94a3b8",fontFamily:"sans-serif",marginBottom:12}}>Max 30 characters each</div>
                {AD_COPY.headlines.map((h,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:"#f8faff",borderRadius:8,marginBottom:6}}>
                    <div style={{width:20,height:20,borderRadius:"50%",background:"#eef2ff",color:"#6366f1",fontSize:10,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif",flexShrink:0}}>{i+1}</div>
                    <span style={{flex:1,fontSize:13,color:"#1e293b",fontFamily:"sans-serif"}}>{h}</span>
                    <span style={{fontSize:10,fontFamily:"sans-serif",color: h.length>28?"#ef4444":"#94a3b8",flexShrink:0}}>{h.length}/30</span>
                  </div>
                ))}
                <CopyBtn text={AD_COPY.headlines.join("\n")} copyKey="headlines" label="Copy All Headlines" />
              </div>
              <div style={{flex:"1 1 220px",minWidth:200}}>
                <div style={{fontSize:14,fontWeight:700,color:"#1e293b",fontFamily:"sans-serif",marginBottom:4}}>Descriptions</div>
                <div style={{fontSize:11,color:"#94a3b8",fontFamily:"sans-serif",marginBottom:12}}>Max 90 characters each</div>
                {AD_COPY.descriptions.map((d,i)=>(
                  <div key={i} style={{display:"flex",gap:8,padding:"10px 10px",background:"#f8faff",borderRadius:8,marginBottom:6,alignItems:"flex-start"}}>
                    <div style={{width:20,height:20,borderRadius:"50%",background:"#eef2ff",color:"#6366f1",fontSize:10,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif",flexShrink:0}}>{i+1}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,color:"#1e293b",fontFamily:"sans-serif",lineHeight:1.5,marginBottom:4}}>{d}</div>
                      <span style={{fontSize:10,fontFamily:"sans-serif",color: d.length>85?"#ef4444":"#94a3b8"}}>{d.length}/90</span>
                    </div>
                  </div>
                ))}
                <CopyBtn text={AD_COPY.descriptions.join("\n")} copyKey="descs" label="Copy All Descriptions" />
              </div>
              <div style={{flex:"1 1 180px",minWidth:160}}>
                <div style={{fontSize:14,fontWeight:700,color:"#1e293b",fontFamily:"sans-serif",marginBottom:4}}>Suggested CTAs</div>
                <div style={{fontSize:11,color:"#94a3b8",fontFamily:"sans-serif",marginBottom:12}}>Pick one for your ad</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:12}}>
                  {AD_COPY.ctas.map((cta,i)=><div key={i} style={{fontSize:13,fontFamily:"sans-serif",color:"#fff",background:"linear-gradient(135deg,#6366f1,#818cf8)",borderRadius:8,padding:"8px 16px",fontWeight:700}}>{cta}</div>)}
                </div>
                <CopyBtn text={AD_COPY.ctas.join("\n")} copyKey="ctas" label="Copy CTAs" />
              </div>
            </div>
          </Section>
        </div>

        {/* Next step block */}
        <div style={{background:"#fff",borderRadius:16,border:"1px solid #e2e8f0",padding:"24px 28px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:20,marginBottom:16}}>
          <div><div style={{fontSize:17,fontWeight:700,color:"#0f172a",marginBottom:6,fontFamily:"sans-serif"}}>Your campaign draft is ready.</div><div style={{fontSize:13,color:"#64748b",fontFamily:"sans-serif",lineHeight:1.5}}>Next, we'll guide you through setting it up inside Google Ads.</div></div>
          <div style={{display:"flex",flexDirection:"column",gap:10,minWidth:200}}>
            <button style={s.primaryBtn} onClick={()=>navigate("/launch-guide")}>Continue to Launch Guide →</button>
            <button style={s.ghostBtn} onClick={()=>navigate("/setup")}>Edit Campaign Inputs</button>
          </div>
        </div>
        <div style={{height:120}} />
      </div>

      <div style={s.fixedFooter}>
        <button style={s.fixedCta} onClick={()=>navigate("/launch-guide")}>Continue to Launch Guide →</button>
      </div>

      <nav style={s.bottomNav}>
        {navItems.map(n=>{
          const on=activeNav===n.id;
          return <button key={n.id} style={{...s.navItem,color: on?"#6366f1":"#94a3b8"}} onClick={()=>{setActiveNav(n.id);navigate(n.path);}}>
            <span style={{fontSize:20}}>{n.icon}</span>
            <span style={{fontSize:10,fontFamily:"sans-serif",fontWeight: on?700:400,color: on?"#6366f1":"#94a3b8"}}>{n.label}</span>
          </button>;
        })}
      </nav>
    </div>
  );
}

const s = {
  page:         {minHeight:"100vh",background:"#f8faff",fontFamily:"'Georgia',serif",display:"flex",flexDirection:"column"},
  header:       {display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 28px",background:"#fff",borderBottom:"1px solid #e2e8f0",boxShadow:"0 1px 8px rgba(0,0,0,0.05)",position:"sticky",top:0,zIndex:300},
  logo:         {display:"flex",alignItems:"center",gap:10},
  mark:         {width:32,height:32,borderRadius:8,background:"linear-gradient(135deg,#6366f1,#818cf8)",color:"#fff",fontWeight:800,fontSize:15,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif"},
  logoTxt:      {fontSize:16,fontWeight:700,color:"#1e293b",fontFamily:"sans-serif"},
  stepPill:     {fontSize:12,fontWeight:700,color:"#6366f1",background:"#eef2ff",padding:"4px 12px",borderRadius:20,fontFamily:"sans-serif"},
  avatar:       {width:32,height:32,borderRadius:"50%",background:"linear-gradient(135deg,#f59e0b,#fbbf24)",color:"#fff",fontWeight:700,fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif"},
  backBtn:      {background:"none",border:"none",fontSize:13,fontWeight:600,color:"#6366f1",cursor:"pointer",fontFamily:"sans-serif",padding:"6px 8px"},
  barBg:        {height:4,borderRadius:99,background:"#e2e8f0",overflow:"hidden"},
  barFill:      {height:"100%",borderRadius:99,background:"linear-gradient(90deg,#6366f1,#818cf8)"},
  body:         {flex:1,padding:"28px 28px 0",maxWidth:1100,margin:"0 auto",width:"100%",boxSizing:"border-box"},
  confirmBlock: {background:"linear-gradient(135deg,#6366f1,#4f46e5)",borderRadius:20,padding:"28px 32px",marginBottom:28,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:20,boxShadow:"0 8px 32px rgba(99,102,241,0.3)"},
  twoCol:       {display:"flex",gap:24,alignItems:"flex-start",flexWrap:"wrap",marginBottom:24},
  colLeft:      {flex:"0 0 340px",minWidth:280,display:"flex",flexDirection:"column",gap:16},
  colRight:     {flex:1,minWidth:280,display:"flex",flexDirection:"column",gap:16},
  primaryBtn:   {padding:"13px 24px",borderRadius:10,background:"linear-gradient(135deg,#6366f1,#818cf8)",color:"#fff",border:"none",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"sans-serif",boxShadow:"0 4px 16px rgba(99,102,241,0.3)"},
  ghostBtn:     {padding:"11px 24px",borderRadius:10,border:"1.5px solid #e2e8f0",background:"#fff",fontSize:13,fontWeight:600,color:"#64748b",cursor:"pointer",fontFamily:"sans-serif"},
  fixedFooter:  {position:"fixed",bottom:60,left:0,right:0,background:"rgba(248,250,255,0.97)",backdropFilter:"blur(8px)",borderTop:"1px solid #e2e8f0",padding:"14px 28px",zIndex:200},
  fixedCta:     {width:"100%",maxWidth:1044,display:"block",margin:"0 auto",padding:"15px",borderRadius:12,background:"linear-gradient(135deg,#6366f1,#818cf8)",color:"#fff",border:"none",fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"sans-serif",boxShadow:"0 4px 20px rgba(99,102,241,0.35)"},
  bottomNav:    {position:"fixed",bottom:0,left:0,right:0,background:"#fff",borderTop:"1px solid #e2e8f0",display:"flex",justifyContent:"space-around",padding:"6px 0 10px",boxShadow:"0 -2px 16px rgba(0,0,0,0.06)",zIndex:300},
  navItem:      {display:"flex",flexDirection:"column",alignItems:"center",gap:2,background:"none",border:"none",cursor:"pointer",padding:"4px 10px"},
};